import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePatchDraftPlot } from '@/api/rest/resources/plot';
import {
  CropsEnum,
  DraftPlot,
  LandManagementProcessEnum,
  PlotOwnershipEnum,
  PlotType,
} from '@/api/rest/resources/types/plot';

export type EditPlotFormValues = {
  name: string;
  land_type: PlotType;
  crops: CropsEnum[];
  land_steward_name: string;
  land_management_process: LandManagementProcessEnum;
  ownership: PlotOwnershipEnum | '';
  label_ids: string[];
};

type UseEditPlotFormParams = {
  plot: DraftPlot;
  onSaved: (updated: DraftPlot) => void;
};

export const useEditPlotForm = ({ plot, onSaved }: UseEditPlotFormParams) => {
  const { t } = useTranslation();
  const { mutateAsync: patchPlot, isPending: isSaving } = usePatchDraftPlot();

  const form = useForm<EditPlotFormValues>({
    defaultValues: {
      name: plot.name,
      land_type: plot.land_type,
      crops: plot.crops ?? [],
      land_steward_name: plot.land_steward_name ?? '',
      land_management_process: plot.land_management_process ?? LandManagementProcessEnum.agriculture,
      ownership: plot.ownership ?? '',
      label_ids: plot.label_ids ?? [],
    },
  });

  const save = useCallback(
    async (values: EditPlotFormValues) => {
      const updated = await patchPlot({
        pathVariables: { id: plot.id },
        bodyData: {
          name: values.name,
          land_type: values.land_type,
          crops: values.crops,
          land_steward_name: values.land_steward_name,
          land_management_process: values.land_management_process,
          ownership: values.ownership,
          label_ids: values.label_ids,
        },
      });
      onSaved(updated);
    },
    [patchPlot, plot.id, onSaved],
  );

  const handleSubmit = form.handleSubmit(save);

  return { form, handleSubmit, isSaving, saveErrorMessage: t('shared.uploadPlots.editPlot.saveFailed') };
};
